import { useEffect, useState } from "react"
import { apiRequest } from "../utils/apiRequest"
import { slugifyCategory } from "../lib/seo"

/**
 * Resolves a URL slug (e.g. "zetdc", "econet-airtime") to its category record.
 *
 * Shared by the /products/:category listing route and the /buy/:slug
 * category-as-product route, which previously each had their own copy of this lookup.
 *
 * NB: this lives in hooks/ rather than lib/seo.js because lib/seo.js is imported by the
 * Node build scripts (generate-sitemap.js, prerender-static.js) — pulling React into it
 * would break them.
 *
 * @returns {{ category: object|null, section: object|null, loading: boolean }}
 *   Both are null while loading. A slug resolves to EITHER a category or a
 *   nav_section (never both) — see the lookup below for why the order matters.
 */
export default function useCategoryBySlug(slug) {
  const [category, setCategory] = useState(null)
  const [section, setSection] = useState(null)
  const [loading, setLoading] = useState(Boolean(slug))

  useEffect(() => {
    if (!slug) {
      setCategory(null)
      setSection(null)
      setLoading(false)
      return
    }

    let active = true
    setLoading(true)

    const target = String(slug).toLowerCase()

    // A slug can name a CATEGORY ("econet-airtime") or a NAV SECTION
    // ("telecel"). The header nav links to section slugs, and five of the six
    // matched no category at all — so the page fell back to the unfiltered
    // product list, which Econet dominates. That is the "it only shows Econet"
    // report. Sections are checked second so an exact category match always
    // wins, keeping every existing /products/<category> URL behaving as before.
    Promise.all([
      apiRequest({ method: "get", url: "/popular_categories?limit=1000", suppressNotifications: true })
        .catch(() => null),
      apiRequest({ method: "get", url: "/nav_sections?scope=all", suppressNotifications: true })
        .catch(() => null),
    ])
      .then(([catRes, navRes]) => {
        if (!active) return

        const categories = catRes?.data?.categories || []
        const match = categories.find((c) => slugifyCategory(c.name) === target)
        if (match) {
          setCategory(match)
          setSection(null)
          return
        }

        const sections = navRes?.data?.sections || []
        setSection(sections.find((sec) => String(sec.slug).toLowerCase() === target) || null)
        setCategory(null)
      })
      .catch(() => {
        if (active) { setCategory(null); setSection(null) }
      })
      .finally(() => {
        if (active) setLoading(false)
      })

    return () => {
      active = false
    }
  }, [slug])

  return { category, section, loading }
}

/**
 * A category with no child products IS the sellable item (isconv): ZESA tokens,
 * Nyaradzo, variable-amount airtime. Those are bought at /buy/<slug>; categories that
 * do have products are browsed at /products/<slug>.
 */
export const isCategoryAsProduct = (category) => Boolean(category) && !category.hasProducts

/** Canonical purchase path for a category-as-product. */
export const buyPathForCategory = (category) =>
  category ? `/buy/${slugifyCategory(category.name)}` : null
